import { useResizeObserver } from '@databases/react';
import React, {
  type FunctionComponent,
  type ReactNode,
  useEffect,
  useState
} from 'react';
import styled, { css } from 'styled-components';

const Container = styled.div<{
  isVisible: boolean;
  isInitialized: boolean;
  width: number;
}>`
  overflow: hidden;

  // Remove margin-left when used inside button group
  transition: ${(props) => (props.isInitialized ? 'margin 0.4s' : 'none')};
  ${(props) => (props.isVisible ? '' : 'margin-left: 0;')}

  > div {
    transition: ${(props) => (props.isInitialized ? 'margin 0.4s' : 'none')};
    margin-right: ${(props) => (props.isVisible ? '0' : `-${props.width}px`)};
  }

  ${(props) =>
    props.isInitialized
      ? ''
      : // eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
        css`
          position: absolute;
          left: -10000px;
        `}
`;

export interface SlideTransitionProps {
  isVisible: boolean;
  children: ReactNode;
}

/**
 * Slides its contents in/out from/to the right when changing its visibility state.
 */
const SlideTransition: FunctionComponent<SlideTransitionProps> = ({
  isVisible,
  children
}) => {
  // In total this component has to be rendered three times:
  // 1. isInitialized is false and width is undefined. The element is absolutely positioned outside of the viewport
  //    so that it is not visible but its size can be calculated.
  // 2. width is now defined. The element is statically positioned and its margin-right value is set to its negative
  //    width to hide it (transition is still disabled).
  // 3. isInitialized is set to true. Transitions are now enabled so that a future showing/hiding of the element is
  //    animated.

  const [element, setElement] = useState<HTMLDivElement | null>(null);
  // Enable useBoundingClientRect to get the correct width of the button element.
  // Otherwise, the width will not be precise enough (digits after the decimal point are not required).
  const width = useResizeObserver(element, false, true)?.width;
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    if (width != null && !isInitialized) {
      setIsInitialized(true);
    }
  }, [isInitialized, width]);

  return (
    <Container isVisible={isVisible} width={width ?? 0} isInitialized={isInitialized}>
      <div ref={setElement}>{children}</div>
    </Container>
  );
};

export default SlideTransition;
